import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Formik, Form, Field } from "formik";
import {
  scrollPageTop,
  loadStorage,
  automateOTPFields,
  selectInputText,
  getQueryData,
  limitizeValue,
  removeKSACountryCode,
  concatOTPDigits
} from "../../util/common";
import { ksaCountryCode, repairStatus } from "../../util/constants";
import { AppointmentService, UserService } from "../../services";
import CarRepairProgress from "./CarRepairProgress";
import { Loader, NotificationHelper, FormTogglers } from "../shared";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import EstimateProgress from "./EstimateProgress";
import QuotationProgress from "./QuotationProgress";
import StepperChart from "./StepperChart";
import { EstimationRequestService, QuotationService } from "../../services";
import AddonsDisplay from "./addonsFlow";
import { useDispatch, useSelector } from "react-redux";
import { saveUser } from "../../redux/reducers/userReducer";
import { useLocation } from "react-router-dom";
import { Errors } from "../../util/errorMessage";
import { authOTPValidation, authValidations } from "../../util/validations";
import { persistor } from "../../redux/store";
import { useAppNavigation } from "../../hooks";

const TrackYourRepairProgress = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { toTrackYourProgress } = useAppNavigation();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.details);

  const number = user?.mobilePhone;
  const estimationId = getQueryData("estimationId") || false;
  const draftVals = loadStorage("draftVals", "sessionStorage");

  const [loading, setLoading] = useState(false);
  const [currentSection, setCurrentSection] = useState(1);
  const [showError, setShowError] = useState(false);
  const [progress, setProgress] = useState([]);
  const [fastCollect, setFastCollect] = useState([]);
  const [offers, setOffers] = useState([]);
  const [recommendedOffers, setRecommendedOffers] = useState([]);
  const [variants, setVariants] = useState([]);

  const [cndProgress, setCNDProgress] = useState([]);
  const [cndFastCollect, setCNDFastCollect] = useState([]);
  const [cndOffers, setCNDOffers] = useState([]);
  const [cndVariants, setCNDVariants] = useState([]);

  const [estimations, setEstimations] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [lastOtp, setLastOtp] = useState("");
  const [estimationsFetched, setEstimationsFetched] = useState(false);
  const [mobilePhone, setMobilePhone] = useState("");
  const [addonsView, setAddonsView] = useState(1);
  const [cndAddonsView, setCNDAddonsView] = useState(1);
  const [activeTab, setActiveTab] = useState(
    estimationId ? "estimations" : location.state?.tab || draftVals?.type || "appointments"
  );

  const phoneNumberRef = useRef();
  const otpFirstDigit = useRef();
  const otpSecondDigit = useRef();
  const otpThirdDigit = useRef();
  const otpFourthDigit = useRef();
  const registerFormRef = useRef();

  useEffect(() => {
    if (user.id) setUpPage();
  }, []);

  useEffect(() => {
    phoneNumberRef.current?.focus();
    otpFirstDigit.current?.focus();
  }, [currentSection]);

  useEffect(() => {
    registerFormRef.current?.validateForm();
  }, [t]);

  const tabHandler = (tab) => {
    setShowError(false);
    setLastOtp("");
    setCurrentSection(tab);
  };

  const updateSeenStatusForRecommendedOffers = (recommendedOffers) => {
    try {
      if (!recommendedOffers?.length > 0) return;
      const offerIds = recommendedOffers.map((offer) => offer?._id);
      UserService.updateRecommendedOfferSeenStatus(JSON.stringify(offerIds));
    } catch (err) {
      console.error(err);
    }
  };

  const setUpPage = async () => {
    setLoading(true);
    // tabHandler(3);
    await getProgress();
    await getEstimations();
    await getQuotations();
    await getCNDBookingProgress();
    setLoading(false);
  };

  const getProgress = async () => {
    try {
      const progress = await AppointmentService.getProgress();
      if (progress?.tracks) {
        const progressData = progress.tracks;
        progressData.forEach((car) => {
          car.forEach((est) => {
            let status = est.status && est.status.toLowerCase().replaceAll(" ", "_");
            est.status = status ? repairStatus.appointments[status] : undefined;
          });
        });

        progressData.forEach((carsEstimation) => {
          carsEstimation.sort((a, b) => a.status - b.status);
        });

        setProgress(progressData);
        setFastCollect(progress.fastCollect);
        setOffers(progress.addonOffers[0]?.associatedOffers || []);
        setRecommendedOffers(progress.recommendedOffers || []);
        setVariants(progress.addonVariants);

        if (progress?.recommendedOffers?.length > 0)
          updateSeenStatusForRecommendedOffers(progress.recommendedOffers || []);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const getEstimations = async () => {
    try {
      const estimation = await EstimationRequestService.getEstimationRequest(number);
      if (estimation) {
        setEstimations(estimation);
        setEstimationsFetched(true);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const getQuotations = async () => {
    try {
      const quotations = await QuotationService.getRequestedQuotes(number);
      if (quotations) setQuotations(quotations);
    } catch (e) {
      console.error(e);
    }
  };

  const getCNDBookingProgress = async () => {
    try {
      const cndProgress = await AppointmentService.getCNDAppointment(number);
      if (cndProgress?.track) {
        const progressData = cndProgress.track;
        progressData.forEach((car) => {
          car.forEach((est) => {
            est.status = est.status ? repairStatus.cnd_appointments[est.status] : undefined;
          });
        });

        setCNDProgress(progressData);
        setCNDFastCollect(cndProgress.fastCollect);
        setCNDOffers(cndProgress.addonOffers[0]?.associatedOffers || []);
        setCNDVariants(cndProgress.addonVariants);

        if (cndProgress?.recommendedOffers?.length > 0)
          updateSeenStatusForRecommendedOffers(cndProgress.recommendedOffers || []);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleRegisterUserSubmit = async (values) => {
    setLoading(true);
    const phoneNumber = ksaCountryCode + values.phoneNumber;
    setMobilePhone(phoneNumber);
    try {
      const registerResponse = await UserService.registerUser({
        phoneNumber
      });

      if (registerResponse) {
        tabHandler(2);
        scrollPageTop();
        setShowError(false);
      }
    } catch (e) {
      console.error(e);
      const key = e?.response?.data?.key || "UNEXPECTED_ERROR";
      if (Errors[e?.response?.data?.key] !== undefined) {
        NotificationHelper.error(t(Errors[key]));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyUserSubmit = async (values, { setSubmitting }) => {
    const code = concatOTPDigits(values);

    if (code === lastOtp) return setShowError(true);

    setLoading(true);
    setSubmitting(true);
    setShowError(false);
    setLastOtp(code);

    try {
      const verifyResponse = await UserService.verifyUser(code, mobilePhone);
      if (verifyResponse && verifyResponse.message === "user.verified") {
        dispatch(saveUser(verifyResponse?.user));
        await persistor.flush();
        window.location.reload();
      }
    } catch (e) {
      console.error(e);
      const key = e?.response?.data?.key || "UNEXPECTED_ERROR";
      if (Errors[e?.response?.data?.key] !== undefined) {
        NotificationHelper.error(t(Errors[key]));
        setLastOtp("");
        return;
      }
      setShowError(true);
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const handleTabSelect = (e) => {
    setActiveTab(e);
    if (estimationId) toTrackYourProgress();
  };

  const NoDataAvailable = () => (
    <div className="pt-3">
      <h5 className="no-data">{t("track.noEstimationRequest")}</h5>
    </div>
  );

  const NoAddonsAvailable = () => (
    <div className="col-md-12 col-xl-8">
      <h5 className="no-data mt-4 mb-0">{t("track.noAddonsAvailable")}</h5>
    </div>
  );

  return (
    <>
      <Loader show={loading} />

      <section className="margin">
        <div className="container-fluid p-0">
          <div className="location_heading">
            <h2>{t("track.heading")}</h2>
          </div>
        </div>
      </section>

      {!user.id ? (
        <div className="track-your-progress-main" id="wizard-p-2">
          <div className="wizard-main-wrapper wizard">
            <div className="content clearfix">
              <div className="content-wrapper">
                <div className="wizard-details-wrap-inner">
                  <div className="enter-details-wrap">
                    <div className="enter-details-wrap-inner">
                      <div className="track-form-box">
                        {currentSection === 1 && (
                          <div>
                            <Formik
                              initialValues={{
                                phoneCode: ksaCountryCode,
                                phoneNumber: removeKSACountryCode(mobilePhone) || ""
                              }}
                              validationSchema={authValidations}
                              onSubmit={handleRegisterUserSubmit}
                              innerRef={registerFormRef}>
                              {({ values, errors, touched, dirty }) => (
                                <Form className="p-width">
                                  <h4 className="section-heading title_form mb-2">
                                    {t("track.login")}
                                  </h4>
                                  <p className="text-md-center">{t("track.phoneNumber")}</p>
                                  <div className="row">
                                    <div className="col-md-8 col-lg-6 m-auto">
                                      <div className="phone-no-wrap">
                                        <div>
                                          <Field
                                            type="number"
                                            id="phoneCode"
                                            name="phoneCode"
                                            placeholder={ksaCountryCode}
                                            className="form-control phone-code"
                                            disabled
                                          />
                                        </div>
                                        <div className="w-100">
                                          <Field
                                            type="number"
                                            id="phoneNumber"
                                            name="phoneNumber"
                                            placeholder={t("track.e.g")}
                                            className="form-control border-left-0"
                                            pattern="[0-9]*"
                                            innerRef={phoneNumberRef}
                                            onInput={limitizeValue}
                                          />
                                          {errors.phoneNumber && touched.phoneNumber && (
                                            <span className="red-color">
                                              {t(errors.phoneNumber)}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  {/* If session is expired then show this message */}
                                  {loadStorage("session")?.expired && (
                                    <p className="session-expired">{t("track.sessionExpired")}</p>
                                  )}

                                  <div className="actions clearfix">
                                    <ul role="menu" aria-label="Pagination">
                                      <li
                                        className="mt-4"
                                        aria-hidden="false"
                                        aria-disabled="false">
                                        <FormTogglers
                                          dependentValue={values.phoneNumber}
                                          dirty={dirty}
                                          tabHandler={tabHandler}
                                          t={t}
                                        />
                                      </li>
                                    </ul>
                                  </div>
                                </Form>
                              )}
                            </Formik>
                          </div>
                        )}

                        {currentSection === 2 && (
                          <div>
                            <Formik
                              initialValues={{ code1: "", code2: "", code3: "", code4: "" }}
                              validationSchema={authOTPValidation}
                              onSubmit={handleVerifyUserSubmit}>
                              {({ values }) => (
                                <Form className="p-width">
                                  <div className="verify-content-wrap-inner wizard-sec-width mt-0">
                                    <h4 className="section-heading title_form">
                                      {t("track.verifyAccount")}
                                    </h4>

                                    <div className="form-group">
                                      <p className="text-md-center">
                                        {t("track.receiveActivationCodePara")}
                                      </p>

                                      <div className="row OTP">
                                        <div className="col-3 col-sm-2 col-md-3 col-lg-2">
                                          <div className="box">
                                            <Field
                                              type="number"
                                              id="code1"
                                              name="code1"
                                              placeholder="X"
                                              className="form-control mb-2"
                                              innerRef={otpFirstDigit}
                                              pattern="[0-9]*"
                                              onInput={(e) =>
                                                automateOTPFields(e, otpSecondDigit, setShowError)
                                              }
                                              onFocus={selectInputText}
                                            />
                                          </div>
                                        </div>

                                        <div className="col-3 col-sm-2 col-md-3 col-lg-2">
                                          <div className="box">
                                            <Field
                                              type="number"
                                              id="code2"
                                              name="code2"
                                              placeholder="X"
                                              className="form-control mb-2"
                                              maxLength={1}
                                              innerRef={otpSecondDigit}
                                              pattern="[0-9]*"
                                              onInput={(e) =>
                                                automateOTPFields(e, otpThirdDigit, setShowError)
                                              }
                                              onKeyDown={(e) => automateOTPFields(e, otpFirstDigit)}
                                              onFocus={selectInputText}
                                            />
                                          </div>
                                        </div>

                                        <div className="col-3 col-sm-2 col-md-3 col-lg-2">
                                          <div className="box">
                                            <Field
                                              type="number"
                                              id="code3"
                                              name="code3"
                                              placeholder="X"
                                              className="form-control mb-2"
                                              maxLength={1}
                                              innerRef={otpThirdDigit}
                                              pattern="[0-9]*"
                                              onInput={(e) =>
                                                automateOTPFields(e, otpFourthDigit, setShowError)
                                              }
                                              onKeyDown={(e) =>
                                                automateOTPFields(e, otpSecondDigit)
                                              }
                                              onFocus={selectInputText}
                                            />
                                          </div>
                                        </div>

                                        <div className="col-3 col-sm-2 col-md-3 col-lg-2">
                                          <div className="box">
                                            <Field
                                              type="number"
                                              id="code4"
                                              name="code4"
                                              placeholder="X"
                                              className="form-control mb-2"
                                              maxLength={1}
                                              innerRef={otpFourthDigit}
                                              pattern="[0-9]*"
                                              onInput={(e) =>
                                                automateOTPFields(e, null, setShowError)
                                              }
                                              onKeyDown={(e) => automateOTPFields(e, otpThirdDigit)}
                                              onFocus={selectInputText}
                                            />
                                          </div>
                                        </div>

                                        {showError && (
                                          <span className="red-color mt-2">
                                            {t("track.invalidCode")}
                                          </span>
                                        )}
                                      </div>
                                    </div>

                                    <div className="actions clearfix mt-4">
                                      <ul role="menu" aria-label="Pagination">
                                        <li aria-hidden="false" aria-disabled="false">
                                          <a onClick={() => tabHandler(1)} role="menuitem">
                                            {t("track.back")}
                                          </a>
                                        </li>
                                        <li aria-hidden="false" aria-disabled="false">
                                          <input
                                            type="submit"
                                            role="menuitem"
                                            disabled={
                                              values.code1 === "" ||
                                              values.code2 === "" ||
                                              values.code3 === "" ||
                                              values.code4 === ""
                                            }
                                            value={t("track.verifyButtonText")}
                                          />
                                        </li>
                                      </ul>
                                    </div>
                                  </div>
                                </Form>
                              )}
                            </Formik>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <section className="progress-tracker track-estimate pt-0">
          <Tabs defaultActiveKey={activeTab} onSelect={handleTabSelect}>
            <Tab eventKey="appointments" title={t("track.appoinments")}>
              {progress.length ? (
                <>
                  <StepperChart displayCondition={addonsView === 1} />
                  <div className="row mt-md-5">
                    {addonsView === 1 && (
                      <CarRepairProgress
                        type="appointments"
                        progress={progress}
                        getProgress={getProgress}
                        fastCollect={fastCollect}
                        setLoading={setLoading}
                      />
                    )}
                    {(offers.length || recommendedOffers.length || variants.length) &&
                    activeTab === "appointments" ? (
                      <AddonsDisplay
                        type="appointments"
                        offers={offers}
                        variants={variants}
                        recommendedOffers={recommendedOffers}
                        others={{
                          branchId: variants[0]?.branchId,
                          branchCode: progress[0]?.[0]?.branchCode,
                          carNumberPlate:
                            progress[0][0].plateNumber + "-" + progress[0][0].plateLetter,
                          make: progress[0][0]?.make,
                          model: progress[0][0]?.model
                        }}
                        addonsView={addonsView}
                        setAddonsView={setAddonsView}
                      />
                    ) : (
                      <NoAddonsAvailable />
                    )}
                  </div>
                </>
              ) : !progress.length && !loading ? (
                <NoDataAvailable />
              ) : null}
            </Tab>
            <Tab eventKey="estimations" title={t("track.estimations")}>
              {estimations.length ? (
                <>
                  <StepperChart />
                  <EstimateProgress
                    estimations={estimations}
                    getEstimations={getEstimations}
                    estimationsFetched={estimationsFetched}
                    estimationId={estimationId}
                  />
                </>
              ) : !estimations.length && !loading ? (
                <NoDataAvailable />
              ) : null}
            </Tab>
            <Tab eventKey="quotations" title={t("track.quotations")}>
              {quotations.length ? (
                <>
                  <StepperChart />
                  <QuotationProgress quotations={quotations} />
                </>
              ) : !quotations.length && !loading ? (
                <NoDataAvailable />
              ) : null}
            </Tab>
            <Tab eventKey="cnd_appointments" title={t("track.cndAppointments")}>
              {cndProgress.length ? (
                <>
                  <StepperChart displayCondition={cndAddonsView === 1} />
                  <div className="row mt-md-5">
                    {cndAddonsView === 1 && (
                      <CarRepairProgress
                        type="cnd_appointments"
                        progress={cndProgress}
                        getProgress={getCNDBookingProgress}
                        fastCollect={cndFastCollect}
                        setLoading={setLoading}
                      />
                    )}
                    {(cndOffers.length || cndVariants.length) &&
                    activeTab === "cnd_appointments" ? (
                      <AddonsDisplay
                        type="cnd_appointments"
                        offers={cndOffers}
                        variants={cndVariants}
                        others={{
                          branchId: cndVariants[0]?.branchId,
                          branchCode: cndProgress[0][0].branchCode,
                          carNumberPlate:
                            cndProgress[0][0].plateNumber + "-" + cndProgress[0][0].plateLetter,
                          make: cndProgress[0][0]?.make,
                          model: cndProgress[0][0]?.model
                        }}
                        addonsView={cndAddonsView}
                        setAddonsView={setCNDAddonsView}
                      />
                    ) : (
                      <NoAddonsAvailable />
                    )}
                  </div>
                </>
              ) : !cndProgress.length && !loading ? (
                <NoDataAvailable />
              ) : null}
            </Tab>
          </Tabs>
        </section>
      )}
    </>
  );
};

export default TrackYourRepairProgress;
